import { useContext, useRef } from "react"
import { useTranslation } from "react-i18next"
import { useFeatureFlagEnabled } from "posthog-js/react"
import { toast } from "sonner"
import { useAppDispatch, useAppSelector } from "@/reducers/hooks"
import { selectUiLanguage, updateSettings } from "@/reducers/settingsSlice"
import SettingsApi from "@/api/settingsApi"
import { XmppContext } from "@/stream/xmppClient"
import { captureError } from "@/ErrorHandlers"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { UI_LANGUAGES, type UiLanguage } from "@/lib/i18n/language"
import { languages } from "@/lib/languages"

const flagOf = (code: UiLanguage) =>
  languages.find((language) => language.code === code)?.flag

// The language the app is shown in. Not the "Preferred communication
// language" next to it, which drives message translation.
const InterfaceLanguageSelect = () => {
  const { t } = useTranslation()
  const { client } = useContext(XmppContext)
  const dispatch = useAppDispatch()
  const uiLanguage = useAppSelector(selectUiLanguage)
  const isEnabled = useFeatureFlagEnabled("ui-language") === true
  // A slow, failing save must not revert past a newer pick.
  const latestPickRef = useRef<UiLanguage | undefined>(undefined)

  if (!isEnabled) return null

  const changeLanguage = async (value: string) => {
    const language = value as UiLanguage
    const previous = uiLanguage
    latestPickRef.current = language
    // Shown at once; the PubSub echo of the publish confirms it.
    dispatch(updateSettings({ settings: { uiLanguage: language } }))
    try {
      await SettingsApi.setUiLanguage(client, language)
    } catch (error) {
      if (latestPickRef.current === language) {
        dispatch(updateSettings({ settings: { uiLanguage: previous } }))
      }
      toast.error(t("interfaceLanguage.saveError"))
      captureError(error, {
        origin: "UserAction",
        extra: { message: "setUiLanguage" },
      })
    }
  }

  return (
    <div className="flex items-center justify-between gap-4">
      <Label htmlFor="interface-language" className="text-sm font-normal">
        {t("interfaceLanguage.label")}
      </Label>
      <Select value={uiLanguage ?? ""} onValueChange={changeLanguage}>
        <SelectTrigger
          id="interface-language"
          aria-label={t("interfaceLanguage.label")}
          className="w-44"
        >
          <SelectValue placeholder={t("interfaceLanguage.placeholder")} />
        </SelectTrigger>
        <SelectContent>
          {UI_LANGUAGES.map((language) => (
            <SelectItem key={language} value={language}>
              <span className="flex items-center gap-2">
                <span>{flagOf(language)}</span>
                <span>{t(`interfaceLanguage.options.${language}`)}</span>
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

export default InterfaceLanguageSelect
